import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { HeaderComponent } from '../../components/header/header.component';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadingComponent } from '../../components/loading/loading.component';
import { TitleComponent } from '../../components/title/title.component';
import { CurrencyDirective } from '../../directives/currency.directive';
import { Table } from '../../domain/models/table';
import { Payment } from '../../domain/models/payment';
import { TableService } from '../../domain/services/table.service';
import { PaymentService } from '../../domain/services/payment.service';
import { PaymentValuesService } from '../../domain/services/payment-values.service';
import { ConfigService } from '../../services/config.service';
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { LocalStorageService } from '../../services/local-storage.service';
import { ItemTableComponent } from './item-table/item-table.component';
import { PayTaxComponent } from './pay-tax/pay-tax.component';
import { RemoveTaxComponent } from './remove-tax/remove-tax.component';
import { TableItem } from '../../domain/models/table-item';
import { OriginType, UserService } from '../../domain/services/user.service';
import { TableValuesSummaryListComponent } from '../../components/table-values-summary-list/table-values-summary-list.component';

/** os itens de uma pessoa no split; name null = sem dono */
interface OwnerSection {
  userId: number | null;
  name: string | null;
  items: TableItem[];
}

@Component({
  selector: 'app-table-details',
  imports: [
    CommonModule,
    FormsModule,
    IconComponent,
    PayTaxComponent,
    RemoveTaxComponent,
    HeaderComponent,
    TitleComponent,
    LoadingComponent,
    ItemTableComponent,
    CurrencyPipe,
    CurrencyDirective,
    TableValuesSummaryListComponent,
  ],
  templateUrl: './table-details.component.html',
  styleUrl: './table-details.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TableDetailsComponent implements OnDestroy {
  uuid: string;
  loading: boolean = false;
  tableSubscription: Subscription;
  tableClosedSubscription: Subscription;

  payTaxModal: NgxSmartModalComponent | null = null;
  removeTaxModal: NgxSmartModalComponent | null = null;
  table: Table;
  ownerSections: OwnerSection[] = [];
  taxPaid: boolean = false;
  customPayment: string;
  canAdvance: boolean = false;
  originalTip?: TableItem;
  tip?: TableItem;
  tipValue = 0
  authModal: NgxSmartModalComponent;
  private authModalSubscription: Subscription | null = null;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ngxSmartModalService: NgxSmartModalService, 
    private vcr: ViewContainerRef,
    private tableService: TableService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private paymentValuesService: PaymentValuesService,
    private configService: ConfigService,
    private paymentService: PaymentService,
    private toastr: ToastrService,
  ) {
    this.payTaxModal = this.ngxSmartModalService.create('pay-tax-modal', PayTaxComponent, this.vcr, {
      customClass: 'modal-btt'
    });

    this.removeTaxModal = this.ngxSmartModalService.create('remove-tax-modal', RemoveTaxComponent, this.vcr, {
      customClass: 'modal-btt'
    });

    this.payTaxModal?.onClose.subscribe((modal: NgxSmartModalComponent) => {
      if(modal.hasData()) {
        const data = modal.getData() as any;
        this.payTaxModal?.removeData();
  
        if(data.remove) {
          this.removeTaxModal?.open();
          return;
        }
      }

      this.updateTable(this.tableService.getTable());
      this.customPayment = '';
      this.keyup();
      this.checkCanAdvance()
    });

    this.removeTaxModal?.onClose.subscribe(() => {
      this.updateTable(this.tableService.getTable());
      this.customPayment = '';
      this.keyup();
      this.checkCanAdvance()
    });

    this.uuid = String(this.route.snapshot.paramMap.get('uuid'));
    this.loading = true;
    
    this.updateTable(this.tableService.getTable(), false);

    if(!this.table) {
      this.tableService.loadTable(this.uuid)
    }
    
    if(!this.tableSubscription) {
      this.tableSubscription = this.tableService.$openedTable.subscribe((table: Table | null) => {
        if(table) {
          this.updateTable(table);
          this.loading = false;
          this.checkCanAdvance()
        }
      })
    }
    if(!this.tableClosedSubscription) {
      this.tableClosedSubscription = this.tableService.$tableClosed.subscribe((closed: boolean) => {
        if(closed) {
          this.localStorageService.removeItem('cart_uuid')
          this.localStorageService.removeItem('table_uuid')
          this.loading = false;
          this.router.navigate([''])
        }
      })
    }
  }

  ngOnDestroy(): void {
    if (this.authModalSubscription) {
      this.authModalSubscription.unsubscribe();
      this.authModalSubscription = null;
    }
    if (this.tableSubscription) {
      this.tableSubscription.unsubscribe();
    }
    if (this.tableClosedSubscription) {
      this.tableClosedSubscription.unsubscribe();
    }
  }

  updateTable(_table: Table, updateTax = true): void {
    // Ignora o param e relê do service agrupado: callers passam a openedTable
    // crua, mas precisamos sempre da versão agrupada (antes um groupProductsTable
    // local rodava 1x no ngOnInit e perdia o agrupamento quando a response do
    // loadTable chegava depois — internet lenta espalhava os produtos).
    const grouped = this.tableService.tableWithGroupedItems();
    if (!grouped) return;
    this.table = grouped;
    this.ownerSections = this.buildOwnerSections();
    this.originalTip = this.table.getTip();
    if(this.originalTip){
      this.updateTip()
    }
    if(updateTax){
      this.taxPaid = this.tableService.getRemoveTax()
    }
  }

  /** Fatia os itens por dono: sem dono primeiro, depois quem está pagando, depois os outros. */
  private buildOwnerSections(): OwnerSection[] {
    if (this.table.paymentType != 'PARTIAL') {
      return [];
    }

    const loggedUserId = this.userService.getUser()?.id;
    const sections = new Map<number | null, OwnerSection>();

    for (const item of this.table.items) {
      if (item.tip) continue;

      const key = item.userId ?? null;
      if (!sections.has(key)) {
        sections.set(key, { userId: key, name: key == null ? null : item.userName, items: [] });
      }
      sections.get(key)!.items.push(item);
    }

    return [...sections.values()].sort((a, b) => {
      if (a.userId == null) return -1;
      if (b.userId == null) return 1;
      if (a.userId === loggedUserId) return -1;
      if (b.userId === loggedUserId) return 1;
      return 0;
    });
  }

  updateTip(): void{
    this.tip = JSON.parse(JSON.stringify(this.originalTip))
    if(this.tip && this.originalTip){
      const proportionalTipValue = this.table.getProportionalValue(this.originalTip.value, this.table.getTotalItensSelectedWithoutTip())
      this.tip.value = Number(proportionalTipValue.toFixed(2))
    }
  }

  handleOpenPayTaxModal() {
    this.payTaxModal?.open();
  }

  keyup() {
    const numericValue = String(this.customPayment)
      .replace('.', '')
      .replace(',', '')
      .replace(/\D/g, '');
    
    const parsedValue = parseFloat(numericValue) / 100;
    if(parsedValue > this.table.remainingValue) {
      const options = { minimumFractionDigits: 2 };
      this.customPayment = 'R$ ' + new Intl.NumberFormat('pt-BR', options).format(this.table.remainingValue);
      this.tableService.setCustomPayment(this.table.remainingValue);
    } else {
      this.tableService.setCustomPayment(parsedValue);
    }
    this.checkCanAdvance()
  }

  get remainingAfterCustomPayment(): number {
    const numericValue = String(this.customPayment || 'R$ 0,00')
      .replace('.', '')
      .replace(',', '')
      .replace(/\D/g, '');
    
    const parsedValue = parseFloat(numericValue) / 100;
    const remaining = this.table.remainingValue - parsedValue;
    return Math.max(0, Math.round(remaining * 100) / 100);
  }

  checkCanAdvance() {
    if(this.table.customPayment) {
      const options = { minimumFractionDigits: 2 };
      this.customPayment = 'R$ ' + new Intl.NumberFormat('pt-BR', options).format(this.table.customPayment);
    } else {
      this.customPayment = 'R$ 0,00';
    }

    if(this.table.paymentType == 'TOTAL') {
      this.canAdvance = true;
    } else if(this.table.paymentType == 'PARTIAL') {
      this.canAdvance = this.table.qtyItensSelected > 0;
    } else if(this.table.paymentType == 'CUSTOM') {
      const numericValue = String(this.customPayment)
        .replace('.', '')
        .replace(',', '')
        .replace(/\D/g, '');
    
      const parsed = parseFloat(numericValue) / 100;
      this.canAdvance = parsed > 0;
    }
    this.updateTip()
  }

  handleBack() {
    this.router.navigate(['..'], {relativeTo: this.route});
  }

  handleNext() {
    const userSituation = this.userService.checkUser();
    if(userSituation == 'AUTHORIZED') {
      this.advanceToPayment();
    } else {
      this.handleAuthenticate()
    }
  }

  handleAuthenticate(origin: OriginType = 'PAY') {
    if (this.authModalSubscription) {
      this.authModalSubscription.unsubscribe();
      this.authModalSubscription = null;
    }

    this.authModal = this.userService.createAuthModal(this.vcr);
    this.authModal.open();
    this.authModalSubscription = this.authModal.onClose.subscribe((res: any) => {
      if(res && res.validated) {
        this.advanceToPayment();
      }
    });
  }

  // No modo tablet (garcom) pula a tela de forma de pagamento e vai direto ao PIX.
  private advanceToPayment(): void {
    if (this.configService.isLandscapeTabletEnabled()) {
      this.payWithPix();
      return;
    }

    this.loading = true;
    this.router.navigate([`${this.uuid}/details/payments`]);
  }

  private payWithPix(): void {
    const pix: Payment = { name: 'PIX', displayName: 'PIX', icon: 'pix', nfceType: 17 };
    this.tableService.setPayment(pix);
    this.loading = true;
    this.paymentService.executePayment(this.table, pix).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigate([`${this.uuid}/payment`]);
      },
      error: (e) => {
        this.loading = false;
        this.toastr.error(
          e?.error?.message || 'Por favor, tente novamente',
          'Ocorreu um erro ao efetuar o pagamento',
        );
      },
    });
  }

  getSubtotal(): number {
    return this.paymentValuesService.getSubtotal(this.table);
  }

  getTotal(): number {
    return this.paymentValuesService.getTotalToPay(this.table);
  }

  getRemainingValue(): number {
    return this.paymentValuesService.getRemainingAfterPayment(this.table);
  }
}
